import { motion, useAnimation } from "framer-motion";
import { useEffect, useRef, useState, useCallback } from "react";
import "../styles/Services_card.css";

/* =========================
   SERVICES DATA
   ========================= */
const services = [
    {
        title: "Computer Sales & Service",
        img: "/ServiceCard_images/sujaramaha_computer.webp",
        desc: "Sales, repair, upgrade, and maintenance of desktops and laptops for home and business use with reliable technical support.",
        link: "/services",
    },
    {
        title: "CCTV Sales & Installation",
        img: "/ServiceCard_images/sujaramaha_cctv.webp",
        desc: "Sales, installation, and maintenance of CCTV surveillance systems for homes, offices, and commercial premises with reliable support.",
        link: "/services",
    },
    {
        title: "Solar Water Heater",
        img: "/ServiceCard_images/sujaramaha_solar.webp",
        desc: "Sales, installation, and maintenance of energy-efficient solar water heater systems for homes and commercial buildings.",
        link: "/services",
    },
    {
        title: "Water Purifier Sales & Service",
        img: "/ServiceCard_images/sujaramaha_water_purifier.webp",
        desc: "Sales, installation, repair, and maintenance of RO, UV, and water purifiers for homes and offices with reliable after-sales support.",
        link: "/services",
    },
    {
        title: "Inverter & Battery Sales & Service",
        img: "/ServiceCard_images/sujaramaha_inverter.webp",
        desc: "Sales, installation, repair, and maintenance of inverters and batteries for homes and offices with reliable power backup solutions.",
        link: "/services",
    },
    {
        title: "Kitchen Chimney Sales & Service",
        img: "/ServiceCard_images/sujaramaha_kitchen_chimney.webp",
        desc: "Sales, installation, repair, and maintenance of kitchen chimneys for smoke-free and hygienic cooking in homes and commercial kitchens.",
        link: "/services",
    },
    {
        title: "All Types of Insurances",
        img: "/ServiceCard_images/sujaramaha_types_general_insurance.webp",
        desc: "Complete insurance solutions including vehicle, health, life, travel, home, and business insurance with expert consultation and end-to-end claim support.",
        link: "/services",
    },
];

/* =========================
   COMPONENT
   ========================= */
export default function ServicesCard() {
    const controls = useAnimation();
    const sectionRef = useRef(null);

    const [xPos, setXPos] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const startX = useRef(0);

    /* ✅ FIX: detect touch device ONCE */
    const isTouchDevice = useRef(false);

    useEffect(() => {
        isTouchDevice.current =
            window.matchMedia("(pointer: coarse)").matches;
    }, []);

    /* ▶ START AUTO SCROLL */
    const startAuto = useCallback(() => {
        controls.start({
            x: [xPos, "-50%"],
            transition: {
                duration: 30,
                ease: "linear",
                repeat: Infinity,
            },
        });
    }, [controls, xPos]);

    /* ⏸ STOP AUTO SCROLL */
    const stopAuto = useCallback(() => {
        controls.stop();
    }, [controls]);

    /* 👀 RUN ONLY WHEN SECTION IS VISIBLE */
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => (entry.isIntersecting ? startAuto() : stopAuto()),
            { threshold: 0.3 }
        );

        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, [startAuto, stopAuto]);

    /* ◀ ▶ MANUAL BUTTON NAV */
    const slideBy = (direction) => {
        stopAuto();

        const offset = 340;
        const nextX =
            direction === "left" ? xPos + offset : xPos - offset;

        setXPos(nextX);
        controls.start({ x: nextX, transition: { duration: 0.4 } });

        // ✅ FIX: do NOT auto-resume on touch devices
        if (!isTouchDevice.current) {
            setTimeout(startAuto, 2500);
        }
    };

    /* 📱 TOUCH / DRAG SUPPORT */
    const onPointerDown = (e) => {
        stopAuto();
        setIsDragging(true);
        startX.current = e.clientX;
    };

    const onPointerMove = (e) => {
        if (!isDragging) return;
        controls.set({ x: xPos + (e.clientX - startX.current) });
    };

    const onPointerUp = (e) => {
        if (!isDragging) return;

        setIsDragging(false);
        const diff = e.clientX - startX.current;

        const nextX =
            Math.abs(diff) > 50
                ? xPos + (diff > 0 ? 300 : -300)
                : xPos;

        setXPos(nextX);
        controls.start({ x: nextX, transition: { duration: 0.3 } });

        // ✅ FIX: prevent restart on mobile tap
        if (!isTouchDevice.current) {
            setTimeout(startAuto, 2000);
        }
    };

    /* 🖱 HOVER PAUSE (DESKTOP ONLY) */
    const handleMouseEnter = () => {
        if (isTouchDevice.current) return; // ✅ FIX
        stopAuto();
    };

    const handleMouseLeave = () => {
        if (isTouchDevice.current) return; // ✅ FIX
        startAuto();
    };

    /* =========================
       RENDER
       ========================= */
    return (
        <section className="services-section" ref={sectionRef}>

            {/* HEADER */}
            <div className="services-header">
                <div>
                    <h2 className="section-title">Our Services</h2>
                    <span className="title-underline"></span>
                </div>

                {/* CONTROLS */}
                <div className="carousel-controls">
                    <button
                        onClick={() => slideBy("left")}
                        aria-label="Previous services"
                    >
                        ‹
                    </button>
                    <button
                        onClick={() => slideBy("right")}
                        aria-label="Next services"
                    >
                        ›
                    </button>
                </div>
            </div>

            {/* CAROUSEL */}
            <div className="carousel-wrapper">
                <motion.div
                    className="services-track"
                    animate={controls}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerLeave={onPointerUp}
                    style={{ touchAction: "pan-y" }}
                >
                    {[...services, ...services].map((service, index) => (
                        <a
                            key={index}
                            href={service.link}
                            className="service-card"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div className="service-image">
                                <img
                                    src={service.img}
                                    alt={service.title}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>

                            <div className="service-content">
                                <h3>{service.title}</h3>
                                <span className="card-underline"></span>
                                <p>{service.desc}</p>
                                <span className="read-more">Read More →</span>
                            </div>
                        </a>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
